/* ============================================================
   Corridor Emissions — GLEC v3 / ISO 14083

   Multi-leg transport chain, because a real Mombasa consignment is a
   chain: vessel, then rail or road line haul, then last mile. Reporting
   only the leg you happen to control is the most common way this figure
   gets quoted wrongly.
   ============================================================ */

import {
  MODE_DEFAULTS,
  FUEL_FACTORS,
  assessChain,
  compareMode,
  equivalences,
  QUALITY_META
} from '../../data/emissions.js';
import { html, raw } from '../../shared/html.js';
import { icon } from '../../shared/icons.js';
import { number, percent, parseNumber } from '../../shared/format.js';
import { saveRecord } from '../../shared/records.js';
import { PrintButton } from '../../components/PrintButton.js';

let legCounter = 0;

export function EmissionsPage() {
  const page = document.createElement('div');
  page.className = 'container-narrow page animate-fade-in';

  page.innerHTML = html`
    <header class="page-head">
      <h1>Corridor Emissions</h1>
      <p>
        gCO₂e per tonne-kilometre on the GLEC Framework method, aligned with ISO 14083:2023 —
        the figure shippers are beginning to ask for, with the data-quality level stated
        alongside it.
      </p>
    </header>

    <div id="em-form">
      <div class="notice notice-info" style="margin-bottom: var(--ca-space-5);">
        <span style="flex-shrink:0;margin-top:2px;">${raw(icon('info', 15))}</span>
        <div style="font-size: var(--ca-text-xs);">
          Enter each leg of the journey. If you know the fuel actually burned, enter it — that
          is <strong style="font-weight: var(--ca-weight-medium);">primary data</strong> and it
          is what a shipper will value. Leave fuel blank and the GLEC default for the mode is
          used instead, which is legitimate but weaker evidence.
        </div>
      </div>

      <h2 class="section-title">Transport chain</h2>
      <div id="legs" class="stack" style="margin-bottom: var(--ca-space-4);"></div>

      <button class="btn btn-secondary btn-block" id="add-leg" type="button"
              style="margin-bottom: var(--ca-space-5);">
        Add another leg
      </button>

      <div id="em-error"></div>

      <button class="btn btn-primary btn-block" id="em-calculate" type="button"
              style="padding: var(--ca-space-4);">
        Calculate emissions
      </button>
    </div>

    <div id="emissions-results" hidden></div>
  `;

  const legsWrap = page.querySelector('#legs');
  addLeg(legsWrap, { mode: 'sea-container', distance: 8500, payload: 24 });
  addLeg(legsWrap, { mode: 'road-artic', distance: 485, payload: 24 });

  page.querySelector('#add-leg').addEventListener('click', () => addLeg(legsWrap));
  page.querySelector('#em-calculate').addEventListener('click', () => calculate(page));

  return page;
}

function addLeg(container, preset = {}) {
  const id = `leg-${++legCounter}`;

  const card = document.createElement('div');
  card.className = 'card';
  card.dataset.leg = id;

  card.innerHTML = html`
    <div class="row" style="margin-bottom: var(--ca-space-3);">
      <h3 style="flex:1; font-size: var(--ca-text-sm);">Leg</h3>
      <button class="btn btn-ghost btn-sm" data-remove type="button" aria-label="Remove this leg">
        ${raw(icon('close', 15))}
      </button>
    </div>

    <label class="field">
      <span class="field-label">Mode</span>
      <select class="input-field" data-field="mode">
        ${Object.entries(MODE_DEFAULTS).map(
          ([key, m]) =>
            html`<option value="${key}" ${key === preset.mode ? 'selected' : ''}>${m.label}</option>`
        )}
      </select>
      <span class="field-hint" data-hint></span>
    </label>

    <div class="grid-2">
      <label class="field">
        <span class="field-label">Distance (km)</span>
        <input class="input-field num" data-field="distance" type="number" inputmode="numeric"
               min="1" step="1" value="${preset.distance ?? ''}" placeholder="485" />
      </label>
      <label class="field">
        <span class="field-label">Payload (tonnes)</span>
        <input class="input-field num" data-field="payload" type="number" inputmode="decimal"
               min="0.1" step="0.1" value="${preset.payload ?? ''}" placeholder="24" />
      </label>
    </div>

    <div class="grid-2">
      <label class="field" style="margin-bottom:0;">
        <span class="field-label" style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
          Fuel burned (litres)
        </span>
        <input class="input-field num" data-field="fuelLitres" type="number" inputmode="decimal"
               min="0" step="1" placeholder="optional" />
      </label>
      <label class="field" style="margin-bottom:0;">
        <span class="field-label" style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
          or rate (L/100 km)
        </span>
        <input class="input-field num" data-field="fuelRate" type="number" inputmode="decimal"
               min="0" step="0.1" placeholder="optional" />
      </label>
    </div>
  `;

  const select = card.querySelector('[data-field="mode"]');
  const hint = card.querySelector('[data-hint]');
  const paintHint = () => {
    const info = MODE_DEFAULTS[select.value];
    hint.textContent = info ? `${info.note} GLEC default: ${info.intensity} gCO₂e/t-km.` : '';
  };
  select.addEventListener('change', paintHint);
  paintHint();

  card.querySelector('[data-remove]').addEventListener('click', () => {
    if (container.children.length > 1) card.remove();
  });

  container.appendChild(card);
}

function readLegs(page) {
  return [...page.querySelectorAll('[data-leg]')].map((card) => {
    const get = (name) => card.querySelector(`[data-field="${name}"]`);
    return {
      mode: get('mode').value,
      distance: parseNumber(get('distance').value),
      payload: parseNumber(get('payload').value),
      fuelLitres: parseNumber(get('fuelLitres').value),
      fuelRate: parseNumber(get('fuelRate').value),
      fuelType: 'diesel'
    };
  });
}

function calculate(page) {
  const legs = readLegs(page);
  const errors = [];

  legs.forEach((leg, i) => {
    if (leg.distance === null || leg.distance <= 0) errors.push(`Leg ${i + 1}: enter a distance.`);
    if (leg.payload === null || leg.payload <= 0) errors.push(`Leg ${i + 1}: enter a payload.`);
  });

  const errorSlot = page.querySelector('#em-error');
  if (errors.length > 0) {
    errorSlot.innerHTML = html`
      <div class="notice notice-danger" style="margin-bottom: var(--ca-space-4);">
        <span style="flex-shrink:0;margin-top:2px;">${raw(icon('alert', 15))}</span>
        <div>
          <strong style="font-weight: var(--ca-weight-medium);">Cannot calculate yet</strong>
          <ul style="margin: var(--ca-space-1) 0 0 var(--ca-space-4); font-size: var(--ca-text-xs);">
            ${errors.map((e) => html`<li>${e}</li>`)}
          </ul>
        </div>
      </div>
    `;
    errorSlot.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    return;
  }
  errorSlot.innerHTML = '';

  const chain = assessChain(legs);
  if (!chain) return;

  renderResults(page, chain);

  void saveRecord('emissions', {
    totalKg: chain.totalKg,
    intensity: chain.intensity,
    quality: chain.quality,
    legs: chain.legs.length
  });
}

function renderResults(page, chain) {
  page.querySelector('#em-form').hidden = true;

  const results = page.querySelector('#emissions-results');
  results.hidden = false;
  results.className = 'animate-fade-in';
  results.replaceChildren();

  const quality = QUALITY_META[chain.quality];
  const eq = equivalences(chain.totalKg);

  /* ---- Headline ---- */
  const head = document.createElement('div');
  head.style.cssText = 'text-align:center; margin-bottom: var(--ca-space-6);';
  head.innerHTML = html`
    <div class="badge" style="background: color-mix(in srgb, ${quality.tone} 13%, transparent); color:${quality.tone};">
      ${quality.label.toUpperCase()} DATA
    </div>
    <div class="stat-figure num" style="margin-top: var(--ca-space-3);">
      ${number(chain.intensity, 1)}
    </div>
    <div style="font-size: var(--ca-text-sm); color: var(--ca-text-tertiary); margin-top: var(--ca-space-1);">
      gCO₂e per tonne-kilometre, well-to-wheel
    </div>
    <div style="font-size: var(--ca-text-xs); color: var(--ca-text-quaternary); margin-top: var(--ca-space-2);">
      <span class="num">${number(chain.totalKg)}</span> kgCO₂e total across
      <span class="num">${number(chain.totalTonneKm)}</span> tonne-km
    </div>
  `;
  results.appendChild(head);

  /* ---- Data quality, stated before the number is used ---- */
  const qualityCard = document.createElement('div');
  qualityCard.className = 'notice';
  qualityCard.classList.add(
    chain.quality === 'primary' ? 'notice-info' : chain.quality === 'modelled' ? 'notice-warning' : 'notice-warning'
  );
  qualityCard.style.marginBottom = 'var(--ca-space-4)';
  qualityCard.innerHTML = html`
    <span style="flex-shrink:0;margin-top:2px;">${raw(icon(chain.quality === 'primary' ? 'check' : 'alert', 15))}</span>
    <div style="font-size: var(--ca-text-xs);">
      <strong style="font-weight: var(--ca-weight-medium);">
        Data quality: ${quality.label.toLowerCase()}
      </strong>
      — ${quality.note}
      ${chain.evidence.emissions > 0 && chain.quality !== 'primary'
        ? html` Measured fuel covers
            <span class="num">${percent(chain.evidence.emissions * 100)}</span> of the emissions
            and <span class="num">${percent(chain.evidence.tonneKm * 100)}</span> of the
            tonne-kilometres — weighted by the work done, not by the leg count, which is how a
            shipper will check it. The chain is reported at the level of its weakest leg, as
            ISO 14083 requires.`
        : ''}
    </div>
  `;
  results.appendChild(qualityCard);

  /* ---- Per leg ---- */
  const legCard = document.createElement('div');
  legCard.className = 'card';
  legCard.style.marginBottom = 'var(--ca-space-4)';
  legCard.innerHTML = html`
    <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-3);">By leg</h3>
    <div class="stack" style="gap: var(--ca-space-3);">
      ${chain.legs.map((leg) => {
        const share = chain.totalKg > 0 ? (leg.kgCO2e / chain.totalKg) * 100 : 0;
        const legQuality = QUALITY_META[leg.quality];
        return html`
          <div style="padding: var(--ca-space-3); border: 1px solid var(--ca-border); border-radius: var(--ca-radius-md);">
            <div class="row" style="margin-bottom: var(--ca-space-2);">
              <span style="flex:1; font-size: var(--ca-text-sm); font-weight: var(--ca-weight-medium);">
                ${leg.modeLabel}
              </span>
              <span class="badge" style="background: color-mix(in srgb, ${legQuality.tone} 13%, transparent); color:${legQuality.tone};">
                ${legQuality.label}
              </span>
            </div>
            <div class="progress-track" style="margin-bottom: var(--ca-space-2);">
              <div class="progress-fill" style="width:${share}%; background: var(--ca-chart-2);"></div>
            </div>
            <div class="row" style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
              <span class="num">${number(leg.distance)} km · ${number(leg.payload, 1)} t</span>
              <span class="spacer"></span>
              <span class="num">${number(leg.kgCO2e)} kgCO₂e · ${share.toFixed(0)}%</span>
            </div>
            <div style="font-size: var(--ca-text-2xs); color: var(--ca-text-quaternary); margin-top: var(--ca-space-1);">
              ${leg.basis}
            </div>
          </div>
        `;
      })}
    </div>
  `;
  results.appendChild(legCard);

  /* ---- Modal shift ---- */
  const roadLegs = chain.legs.filter((l) => l.mode.startsWith('road'));
  const roadTonneKm = roadLegs.reduce((sum, l) => sum + l.tonneKm, 0);
  const roadKg = roadLegs.reduce((sum, l) => sum + l.kgCO2e, 0);
  const railAlternative = compareMode(roadTonneKm, 'rail-diesel');

  if (railAlternative && roadKg > railAlternative.kgCO2e) {
    const saving = roadKg - railAlternative.kgCO2e;
    const shift = document.createElement('div');
    shift.className = 'card';
    shift.style.marginBottom = 'var(--ca-space-4)';
    shift.style.borderColor = 'color-mix(in srgb, var(--ca-positive) 30%, var(--ca-border))';
    shift.innerHTML = html`
      <div class="row" style="margin-bottom: var(--ca-space-3);">
        <span style="color: var(--ca-positive);">${raw(icon('train', 18))}</span>
        <h3 style="flex:1; font-size: var(--ca-text-sm);">Modal shift opportunity</h3>
      </div>
      <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary); margin-bottom: var(--ca-space-3);">
        Moving the road portion of this chain to rail would cut roughly
        <strong class="num" style="color: var(--ca-positive);">${number(saving)} kgCO₂e</strong> —
        about <span class="num">${percent((saving / chain.totalKg) * 100)}</span> of the whole
        journey.
      </p>
      <p style="font-size: var(--ca-text-xs); color: var(--ca-text-quaternary);">
        Computed at GLEC defaults on both sides, so treat it as sizing the prize rather than a
        saving already banked. Rail line haul also excludes drayage at both ends, which adds
        back road kilometres — check the Cost-per-Ton-Km calculator before committing.
      </p>
    `;
    results.appendChild(shift);
  }

  /* ---- Tender statement ---- */
  const statement = document.createElement('div');
  statement.className = 'card card-tinted';
  statement.style.marginBottom = 'var(--ca-space-4)';
  statement.innerHTML = html`
    <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-2);">
      For a tender response
    </h3>
    <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary); font-style: italic; line-height: var(--ca-leading-normal);">
      “Greenhouse gas emissions for this transport chain are
      <span class="num">${number(chain.totalKg)}</span> kgCO₂e, an intensity of
      <span class="num">${number(chain.intensity, 1)}</span> gCO₂e per tonne-kilometre,
      calculated on a well-to-wheel basis in accordance with the GLEC Framework and
      ISO 14083:2023, using ${quality.label.toLowerCase()} data across
      ${chain.legs.length} transport chain ${chain.legs.length === 1 ? 'element' : 'elements'}.”
    </p>
    <p style="font-size: var(--ca-text-xs); color: var(--ca-text-quaternary); margin-top: var(--ca-space-3);">
      Roughly equivalent to <span class="num">${number(eq.carKm)}</span> km in an average
      passenger car, or the annual sequestration of
      <span class="num">${number(eq.treeYears)}</span> mature trees. Equivalences are for
      narrative only — quote the tonne-km intensity, not the trees.
    </p>
  `;
  results.appendChild(statement);

  /* ---- Improving the number ---- */
  if (chain.quality !== 'primary') {
    const improve = document.createElement('div');
    improve.className = 'card';
    improve.style.marginBottom = 'var(--ca-space-4)';
    improve.innerHTML = html`
      <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-2);">
        How to strengthen this
      </h3>
      <ul style="margin: 0 0 0 var(--ca-space-5); font-size: var(--ca-text-sm); color: var(--ca-text-secondary); line-height: var(--ca-leading-normal);">
        <li>Record litres dispensed per trip, not just fuel spend — spend alone cannot produce a primary figure.</li>
        <li>Capture actual payload per trip rather than nominal capacity.</li>
        <li>Ask sub-contracted carriers for their own fuel data; their leg is your weakest link.</li>
        <li>Where a leg is genuinely unmeasurable, keep the default and say so — an honest default beats an invented measurement.</li>
      </ul>
    `;
    results.appendChild(improve);
  }

  const methodNote = document.createElement('p');
  methodNote.style.cssText =
    'font-size: var(--ca-text-2xs); color: var(--ca-text-quaternary); line-height: var(--ca-leading-snug);';
  methodNote.textContent =
    `Well-to-wheel diesel factor ${FUEL_FACTORS.diesel.wtw} kgCO₂e per litre, covering both combustion ` +
    'and fuel production. Mode intensities are GLEC Framework defaults, not Kenyan measurements — ' +
    'they are the correct fallback where primary data is absent, and should be replaced by your own ' +
    'fuel records wherever those exist.';
  results.appendChild(methodNote);

  results.appendChild(
    PrintButton({
      targetId: 'emissions-results',
      title: 'Corridor Emissions Statement',
      subtitle: `${number(chain.intensity, 1)} gCO₂e/t-km · ${quality.label} data · GLEC / ISO 14083`
    })
  );

  const again = document.createElement('div');
  again.className = 'no-print';
  again.style.cssText = 'text-align:center; margin-top: var(--ca-space-4);';
  const btn = document.createElement('button');
  btn.className = 'btn btn-ghost';
  btn.type = 'button';
  btn.textContent = 'Change the chain';
  btn.addEventListener('click', () => {
    results.hidden = true;
    page.querySelector('#em-form').hidden = false;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
  again.appendChild(btn);
  results.appendChild(again);

  results.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
